// Adapter that converts the flat shipping-rates array from the API
// (GET /api/content.store_info.shipping_rates) into the nested
// `{COUNTRY: {methods: [{ id, name_en/ar/fr, transit_en/ar/fr, unit,
// categories: [{ id, price, name_en/ar/fr }] }]}` shape that the
// ShippingCalculator React component consumes.
//
// One nice property: the adapter is pure — given the same API payload,
// it always produces the same shape. So the calculator UI keeps working
// identically whether the rates come from the API (live) or the static
// fallback in `/app/frontend/src/data/rates.js`.
import { CATEGORY_LABELS, METHOD_LABELS, translateEta } from "./shippingI18n";

export function adaptApiRatesToCalc(apiRates) {
  if (!Array.isArray(apiRates) || apiRates.length === 0) return null;

  // Group rates by country and method, preserving sort order from the
  // API list (the admin can re-order rates by editing in admin UI).
  const byCountry = {};
  for (const r of apiRates) {
    if (!r || r.enabled === false) continue;
    const country = r.country;
    const methodId = r.method;
    if (!country || !methodId) continue;

    if (!byCountry[country]) byCountry[country] = { methods: {} };
    if (!byCountry[country].methods[methodId]) {
      const ml = METHOD_LABELS[methodId] || { en: methodId, ar: methodId, fr: methodId };
      const eta = r.eta || "";
      byCountry[country].methods[methodId] = {
        id: methodId,
        name_en: ml.en,
        name_ar: ml.ar,
        name_fr: ml.fr,
        transit_en: eta,
        transit_ar: translateEta(eta, "ar"),
        transit_fr: translateEta(eta, "fr"),
        unit: r.unit || "kg",
        categories: [],
      };
    }
    const catId = r.product_category || "general";
    const cl = CATEGORY_LABELS[catId] || { en: catId, ar: catId, fr: catId };
    byCountry[country].methods[methodId].categories.push({
      id: r.id,
      price: Number(r.price_per_kg_usd) || 0,
      // Per-category unit so a single method (e.g. CN sea) can mix
      // cbm-priced rows and kg-priced rows for the same product type.
      unit: r.unit || byCountry[country].methods[methodId].unit || "kg",
      // Optional secondary additive price (e.g. CN sea clothes/shoes
      // = $200/m³ + $5.5/kg → secondary_price=5.5, secondary_unit=kg).
      secondary_price: (r.secondary_price_usd != null && r.secondary_price_usd > 0)
        ? Number(r.secondary_price_usd) : null,
      secondary_unit: (r.secondary_price_usd != null && r.secondary_price_usd > 0)
        ? (r.secondary_unit || "kg") : null,
      name_en: cl.en,
      name_ar: cl.ar,
      name_fr: cl.fr,
    });
  }

  // Flatten the methods object to an array (calculator expects array)
  const out = {};
  for (const [country, data] of Object.entries(byCountry)) {
    const methods = Object.values(data.methods).filter((m) => m.categories.length > 0);
    if (methods.length) out[country] = { methods };
  }
  return out;
}
